/**
 * AgentIPList - Simple vertical list of agent IP addresses
 *
 * Displays all IP addresses for an agent with copy functionality.
 * Primary IP (first) is visually distinguished with a PRIMARY badge.
 * Used in agent drawer header for immediate IP visibility.
 */

import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { cn } from '@/utils/classname';

interface AgentIPListProps {
  ips: string[];
  className?: string;
}

export const AgentIPList: React.FC<AgentIPListProps> = ({ ips, className }) => {
  if (ips.length === 0) return null;

  const [primaryIP, ...additionalIPs] = ips;

  return (
    <section
      aria-labelledby="ip-addresses-heading"
      className={cn('flex flex-col gap-2', className)}
      data-testid="agent-ip-list"
    >
      <h3
        id="ip-addresses-heading"
        className="text-chariot-text-primary text-sm font-medium"
      >
        IP Addresses
      </h3>

      <div className="flex flex-col gap-1.5">
        {/* Primary IP with badge */}
        <div className="flex items-center gap-2">
          <CopyToClipboard
            className="text-chariot-text-primary font-mono text-sm"
            iconClassName="size-3"
          >
            {primaryIP}
          </CopyToClipboard>
          <span className="bg-chariot-brand-primary/10 text-chariot-brand-primary rounded px-1.5 py-0.5 text-xs font-medium">
            PRIMARY
          </span>
        </div>

        {/* Additional IPs */}
        {additionalIPs.map(ip => (
          <CopyToClipboard
            key={ip}
            className="text-chariot-text-secondary font-mono text-sm"
            iconClassName="size-3"
          >
            {ip}
          </CopyToClipboard>
        ))}
      </div>
    </section>
  );
};
